import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../../api/axios";
import SidebarAdmin from "../../components/SidebarAdmin";
import { parseServerDate, formatLocalDateTime } from "../../utils/dateTime";

export default function AdminDashboard() {
  const [stats, setStats] = useState({ totalUsers: 0, messagesSent: 0, activePlans: 0, systemStatus: "..." });
  const [health, setHealth] = useState({ ok: false, bootedAt: null });
  const [sessionsSummary, setSessionsSummary] = useState({ online: 0, total: 0 });
  const [activity, setActivity] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [supportEmail, setSupportEmail] = useState("");

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [statsRes, healthRes, sessionsRes, logsRes, analyticsRes, settingsRes] = await Promise.all([
          axios.get("/admin/stats"),
          axios.get("/health"),
          axios.get("/admin/whatsapp-sessions"),
          axios.get("/admin/activity-logs"),
          axios.get("/admin/analytics/messages"),
          axios.get("/admin/settings"),
        ]);

        setStats(statsRes.data || { totalUsers: 0, messagesSent: 0, activePlans: 0, systemStatus: "..." });
        setHealth({ ok: Boolean(healthRes.data?.ok), bootedAt: healthRes.data?.bootedAt || null });

        const sessions = sessionsRes.data || [];
        const online = sessions.filter((s) => s.isOnline).length;
        setSessionsSummary({ online, total: sessions.length });

        setActivity((logsRes.data || []).slice(0, 6));
        setDailyStats(analyticsRes.data?.dailyStats || []);
        setSupportEmail(settingsRes.data?.support_email || "support@yourcompany.com");
      } catch (err) {
        console.error("Dashboard fetch error", err);
      }
    };
    fetchDashboard();
  }, []);

  const dailyChart = useMemo(() => {
    if (!dailyStats || dailyStats.length === 0) return [];
    return dailyStats.map((d) => ({
      label: (() => {
        const dateObj = parseServerDate(d.date);
        return dateObj ? dateObj.toLocaleDateString("en-IN", { month: "short", day: "numeric" }) : "—";
      })(),
      total: Number(d.total || 0),
      success: Number(d.success || 0),
      failed: Number(d.failed || 0),
    }));
  }, [dailyStats]);

  const maxTotal = Math.max(1, ...dailyChart.map((d) => d.total));

  const cards = [
    { title: "Total Clients", value: stats.totalUsers, color: "border-blue-600", icon: "👥" },
    { title: "Messages Sent", value: stats.messagesSent, color: "border-green-600", icon: "📩" },
    { title: "Active Plans", value: stats.activePlans, color: "border-purple-600", icon: "💎" },
    { title: "System Status", value: health.ok ? "Online" : stats.systemStatus, color: "border-yellow-600", icon: "⚡" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
      <SidebarAdmin />
      <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-gray-800">Admin Overview</h1>
            <p className="text-sm text-gray-500 mt-1">Real-time system overview and operations</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${health.ok ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {health.ok ? "Backend OK" : "Checking..."}
            </span>
            {health.bootedAt && (
              <span className="text-xs text-gray-500">Booted: {formatLocalDateTime(health.bootedAt)}</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {cards.map((c, i) => (
            <div key={i} className={`bg-white p-4 md:p-6 rounded-2xl shadow-sm border-l-8 ${c.color} hover:shadow-lg transition-all`}>
              <div className="text-2xl md:text-3xl mb-2">{c.icon}</div>
              <p className="text-xs md:text-sm text-gray-500 font-bold uppercase">{c.title}</p>
              <p className="text-2xl md:text-3xl font-black mt-1">{c.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
          <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">Message Activity (Last 7 Days)</h2>
              <Link to="/admin/analytics" className="text-sm text-blue-600 font-bold hover:underline">View Analytics</Link>
            </div>
            {dailyChart.length === 0 ? (
              <div className="text-gray-400 text-sm">No data yet</div>
            ) : (
              <div className="grid grid-cols-7 gap-3 items-end h-44">
                {dailyChart.map((d) => (
                  <div key={d.label} className="flex flex-col items-center gap-2">
                    <div className="w-full bg-gray-100 rounded-lg flex flex-col justify-end h-32">
                      <div
                        className="bg-blue-500 rounded-lg"
                        style={{ height: `${Math.max(8, (d.total / maxTotal) * 100)}%` }}
                        title={`Total: ${d.total}`}
                      />
                    </div>
                    <span className="text-xs text-gray-500">{d.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h2 className="text-lg font-bold text-gray-800 mb-4">System Status</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
                <div>
                  <p className="font-bold text-gray-800">WhatsApp Sessions</p>
                  <p className="text-xs text-gray-500">Online: {sessionsSummary.online} / {sessionsSummary.total}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${sessionsSummary.online > 0 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                  {sessionsSummary.online > 0 ? "Active" : "Idle"}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
                <div>
                  <p className="font-bold text-gray-800">Backend</p>
                  <p className="text-xs text-gray-500">API + DB status</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${health.ok ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                  {health.ok ? "Online" : "Checking"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 gap-3">
              <Link to="/admin/users" className="bg-blue-600 text-white px-4 py-3 rounded-xl font-bold text-center hover:bg-blue-700 transition">Add / Manage Users</Link>
              <Link to="/admin/sessions" className="bg-gray-900 text-white px-4 py-3 rounded-xl font-bold text-center hover:bg-gray-800 transition">WhatsApp Monitor</Link>
              <Link to="/admin/reports" className="bg-green-600 text-white px-4 py-3 rounded-xl font-bold text-center hover:bg-green-700 transition">Download Reports</Link>
              <Link to="/admin/api-keys" className="bg-purple-600 text-white px-4 py-3 rounded-xl font-bold text-center hover:bg-purple-700 transition">API Keys</Link>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">Recent Activity</h2>
              <Link to="/admin/logs" className="text-sm text-blue-600 font-bold hover:underline">View All</Link>
            </div>
            {activity.length === 0 ? (
              <div className="text-gray-400 text-sm">No activity yet</div>
            ) : (
              <div className="space-y-3">
                {activity.map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div>
                      <p className="text-sm font-bold text-gray-800">{a.action}</p>
                      <p className="text-xs text-gray-500">{a.user_email || a.user_name || "System"}</p>
                    </div>
                    <span className="text-xs text-gray-400">{formatLocalDateTime(a.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 mb-2">Support</h2>
          <p className="text-gray-500 text-sm">Need help? Contact support for priority assistance.</p>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-xs text-gray-500">Support Email</p>
              <p className="text-sm font-bold text-gray-800">{supportEmail}</p>
            </div>
            <Link to="/admin/settings" className="bg-blue-600 text-white px-4 py-2 rounded-xl font-bold text-center hover:bg-blue-700 transition">
              Update Support Email
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}